import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MessageComposer = ({ message, onChange, errors }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const messageTemplates = [
    {
      id: 'professional',
      title: 'Professional Introduction',
      content: `Dear [Client Name],\n\nI'm excited to share the custom design concepts we've created for you. Please take your time to review each piece and let me know your thoughts.\n\nThe preview link will be available for 24 hours. If you need more time or have any questions, please don't hesitate to reach out.\n\nBest regards,\n[Your Name]`
    },
    {
      id: 'casual',title: 'Friendly Approach',
      content: `Hi [Client Name]!\n\nYour custom jewelry designs are ready for preview! I can't wait to hear what you think about the concepts we've put together.\n\nTake a look and let me know which direction resonates with you most.\n\nLooking forward to your feedback!\n\n[Your Name]`
    },
    {
      id: 'detailed',title: 'Detailed Explanation',
      content: `Dear [Client Name],\n\nAttached are the design concepts based on our consultation. Each piece has been carefully crafted to meet your specifications:\n\n• Design variations with different stone settings\n• Multiple angle views for comprehensive review\n• Technical specifications included\n\nPlease review at your convenience and provide feedback on your preferred direction.\n\nBest regards,\n[Your Name]`
    }
  ];

  const handleTemplateSelect = (template) => {
    onChange(template?.content);
    setIsExpanded(true);
  };

  const handleMessageChange = (e) => {
    onChange(e?.target?.value);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="MessageSquare" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg font-heading text-foreground">Personal Message</h3>
            <p className="text-sm text-muted-foreground">Add a personalized note for your client</p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </Button>
      </div>
      {!isExpanded && !message && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">Choose a template to get started:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {messageTemplates?.map((template) => (
              <button
                key={template?.id}
                onClick={() => handleTemplateSelect(template)}
                className="p-4 text-left border border-border rounded-lg hover:border-accent/50 hover:bg-accent/5 transition-luxury text-foreground"
              >
                <h4 className="text-sm font-medium text-foreground mb-2">{template?.title}</h4>
                <p className="text-xs text-muted-foreground line-clamp-3">
                  {template?.content?.substring(0, 100)}...
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
      {(isExpanded || message) && (
        <div className="space-y-4">
          {!message && (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-3">Quick templates:</p>
              <div className="flex flex-wrap gap-2">
                {messageTemplates?.map((template) => (
                  <Button
                    key={template?.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    {template?.title}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="relative">
            <textarea
              value={message}
              onChange={handleMessageChange}
              placeholder="Write a personal message to accompany your design preview..."
              className="w-full h-32 p-4 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-luxury text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            />
            
            <div className="absolute bottom-3 right-3 flex items-center space-x-2">
              <span className="text-xs text-muted-foreground">
                {message?.length}/1000
              </span>
            </div>
          </div>

          {errors?.message && (
            <p className="text-sm text-error">{errors?.message}</p>
          )}

          {/* Formatting Tools */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" iconName="Bold">
                <span className="sr-only">Bold</span>
              </Button>
              <Button variant="ghost" size="sm" iconName="Italic">
                <span className="sr-only">Italic</span>
              </Button>
              <Button variant="ghost" size="sm" iconName="Link">
                <span className="sr-only">Link</span>
              </Button>
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Info" size={14} />
              <span>Use [Client Name] and [Your Name] as placeholders</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageComposer;